// In src/common/events.js

import { retractInlineAsk } from './ask.js';

function decodeJwtPayload(token) {
  try {
    const parts = String(token || '').split('.');
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

const BANNER_ID = "__footcheck_session_expired_banner__";

export function showSessionExpiredBanner({ context = "this app" } = {}) {
  if (typeof document === "undefined") return;
  if (document.getElementById(BANNER_ID)) return; // already shown
  const banner = document.createElement("div");
  banner.id = BANNER_ID;
  banner.setAttribute("role", "alert");
  banner.style.cssText = [
    "position:fixed",
    "top:0",
    "left:0",
    "right:0",
    "z-index:2147483647",
    "padding:10px 14px",
    "background:#b22222",
    "color:#fff",
    "font:14px system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
    "box-shadow:0 2px 8px rgba(0,0,0,.25)",
  ].join(";");
  banner.textContent =
    `Your session has expired. Please close and reopen ${context} to sign in again.`;
  try { document.body.appendChild(banner); } catch {}
}

export class EventsManager {
  constructor(apiBase = null, clientId = null, getAccessTokenFn = null) {
    this.apiBase = apiBase;
    this.clientId = clientId;
    this.evt = null;
    this._cid = null;
    this._docId = null;
    this._clientType = null; // Store client_type for retries
    this.currentDocumentId = null;
    this.callbacks = {};
    this._retryMs = 1000;
    this._maxRetryMs = 15000;
    this._consecutiveFailures = 0;
    this._maxAuthFailures = 4;
    this._stopped = false;
    this.getAccessTokenFn = getAccessTokenFn; // Function to retrieve current access token
  }

  // Setters
  setDocumentId(documentId) { this.currentDocumentId = documentId; }
  setApiBase(apiBase) { this.apiBase = apiBase; }
  setClientId(clientId) { this.clientId = clientId; }
  setCallbacks(callbacks) { this.callbacks = callbacks; }
  setGetAccessTokenFn(fn) { this.getAccessTokenFn = fn; }

  _sameWire(clientId) {
    return !!this.evt && this._cid === clientId && this._docId === this.currentDocumentId && this.evt.readyState !== 2;
  }

  async wireSSE(clientId, clientType = null) {
    let token = null;
    let effectiveClientId = clientId || this.clientId;

    // EventSource cannot send Authorization headers. Fetch a fresh token before
    // constructing the URL, then prefer the token's cid. This prevents retry
    // loops where a stale path client_id is paired with a newly refreshed token.
    if (this.getAccessTokenFn) {
      try {
        token = await this.getAccessTokenFn();
        const tokenClientId = decodeJwtPayload(token)?.cid;
        if (tokenClientId) {
          if (effectiveClientId && effectiveClientId !== tokenClientId) {
            console.warn('[EventsManager] SSE client_id/token cid mismatch; using token cid', {
              requested: effectiveClientId,
              tokenCid: tokenClientId
            });
          }
          effectiveClientId = tokenClientId;
          this.clientId = tokenClientId;
        }
      } catch (error) {
        console.warn('[EventsManager] Failed to get access token for SSE:', error);
      }
    }

    if (!effectiveClientId) {
      console.warn('[EventsManager] Cannot wire SSE without client_id');
      return;
    }

    if (this._sameWire(effectiveClientId)) return;

    this.evt?.close();
    this.evt = null;

    // A new client/session should be able to recover after we stopped a stale
    // retry loop, while automatic retries still respect the stopped flag below.
    if (this._stopped && this._cid === effectiveClientId) return;
    if (this._stopped) {
      this._stopped = false;
      this._consecutiveFailures = 0;
      this._retryMs = 1000;
    }
    this._cid = effectiveClientId;
    this._docId = this.currentDocumentId;
    this._clientType = clientType; // Store client_type for retries

    // Build URL with document_id if present
    let url = this.currentDocumentId
      ? `${this.apiBase}/events/${encodeURIComponent(effectiveClientId)}?document_id=${encodeURIComponent(this.currentDocumentId)}`
      : `${this.apiBase}/events/${encodeURIComponent(effectiveClientId)}`;
    
    // Add client_type parameter if provided (to distinguish word-addin from desktop-app)
    if (clientType) {
      const separator = url.includes('?') ? '&' : '?';
      url += `${separator}client_type=${encodeURIComponent(clientType)}`;
    }
    
    // EventSource doesn't support custom headers, so we pass the token as a query param.
    if (token) {
      const separator = url.includes('?') ? '&' : '?';
      url += `${separator}access_token=${encodeURIComponent(token)}`;
    }
      
    const eventSource = new EventSource(url, { withCredentials: false });
    this.evt = eventSource;

    eventSource.addEventListener('open', () => {
      this._retryMs = 1000;
      this._consecutiveFailures = 0;
      this.callbacks.onSseReady?.();
      // This stream is also how the engine registers as a bridge client, so
      // until it opens every bridged command fails with "not connected".
      // Features that call bridged endpoints at startup wait for this.
      window.dispatchEvent(new CustomEvent('fc:bridge-ready', {
        detail: { clientId: effectiveClientId },
      }));
    });

    eventSource.addEventListener('error', () => {
      eventSource.close();
      this.evt = null;
      this._consecutiveFailures += 1;

      // If we're seeing repeated failures without ever opening, the token
      // is almost certainly expired/invalid and refresh is not recovering.
      // Stop the retry storm and surface a session-expired event.
      if (this._consecutiveFailures >= this._maxAuthFailures) {
        if (this._stopped) return;
        this._stopped = true;
        console.warn(`[EventsManager] ${this._consecutiveFailures} consecutive SSE failures — giving up and surfacing session-expired`);
        try {
          if (this.callbacks.onSessionExpired) {
            this.callbacks.onSessionExpired({ reason: "sse_repeated_auth_failure" });
          } else {
            showSessionExpiredBanner({ context: "the app" });
          }
        } catch (e) {
          console.warn("[EventsManager] onSessionExpired callback threw", e);
        }
        return;
      }

      const retryDelay = this._retryMs;
      this._retryMs = Math.min(this._retryMs * 2, this._maxRetryMs);
      setTimeout(() => {
        if (this._stopped) return;
        if (this._cid === effectiveClientId && this._docId === this.currentDocumentId) {
          this.wireSSE(this.clientId || effectiveClientId, this._clientType); // Preserve client_type on retry
        }
      }, retryDelay);
    });

    eventSource.addEventListener('message', (ev) => {
      let payload;
      console.log("Event Source Message:", effectiveClientId);
      try { payload = JSON.parse(ev.data); }
      catch { return; }

      if (payload?.target_client && payload.target_client !== effectiveClientId) return;
      
      const type = payload?.type;

      if (type === "progress") {
        // Handle progress events (from parse_inline_citations, etc.)
        if (this.callbacks.onProgress && payload?.data) {
          this.callbacks.onProgress(payload.data);
        }
      }
      else if (type === "ask_user") {
        // Prefer the main onAskUser callback (which goes to FootnotesManager)
        if (this.callbacks.onAskUser) {
          this.callbacks.onAskUser(payload, effectiveClientId);
        }
        // Fallback for other potential listeners
        else if (this.callbacks.onShowInlineAsk && payload?.footnote_id) {
          this.callbacks.onShowInlineAsk(payload.footnote_id, payload.cite_id, payload.question, payload.question_id, 
            effectiveClientId, this.currentDocumentId, this.apiBase);
        }
        return;
      }

      if (type === "ask_user_retracted") {
        // The agent stopped waiting (timeout or cancelled run): drop the card.
        retractInlineAsk(payload?.question_id);
        return;
      }

      if (type === "inline_ask") {
        // This is a direct command to show an inline prompt
        if (this.callbacks.onShowInlineAsk && payload?.footnote_id) {
          this.callbacks.onShowInlineAsk(payload.footnote_id, payload.cite_id, payload.question, payload.question_id, 
            effectiveClientId, this.currentDocumentId, this.apiBase);
        }
        return;
      }
    });
  }

  disconnect() {
    this.evt?.close();
    this.evt = null;
  }
}